import React, { useState, useEffect, useContext } from 'react';
import useApiStable from '../../utils/useApiStable';
import '../../styles/KnockoutPhase.css';
import { AuthContext } from '../../context/AuthContext';
import KnockoutBracket from './KnockoutBracket';

/**
 * Eigenständige Seite `/knockout-phase`: Turnierauswahl per URL-Parameter bzw. über die Liste "Alle
 * Turniere mit K.O.-Phase" unten, die eigentliche Baum-Darstellung samt Team-Tausch kommt aus dem
 * gemeinsam mit der in die Turnierverwaltung eingebetteten Ansicht genutzten KnockoutBracket.
 */
const KnockoutPhase = () => {
  const api = useApiStable();
  const { user } = useContext(AuthContext);
  const [tournamentId, setTournamentId] = useState(null);
  const [allTournaments, setAllTournaments] = useState([]);

  // Turnier-ID aus URL-Parameter lesen
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tid = urlParams.get('tournamentId');
    if (tid) {
      setTournamentId(parseInt(tid, 10));
    }
  }, []);

  // Alle Turniere mit K.O.-Phase laden
  useEffect(() => {
    const loadAllTournaments = async () => {
      try {
        const tournaments = await api.get('/tournaments');
        const tournamentsWithKnockout = [];

        for (const tournament of tournaments) {
          try {
            const matches = await api.get(`/tournaments/knockout/${tournament.id}/matches`);
            // Prüfe, ob die KO-Phase existiert (API antwortet, auch wenn matches leer ist)
            let currentRound = 'Noch keine KO-Matches';
            let totalMatches = 0;
            let completedMatches = 0;
            if (matches && matches.length > 0) {
              // Ermittle aktuelle Runde
              const incompleteMatches = matches.filter(m => !m.winnerTeam);
              currentRound = 'Abgeschlossen';

              if (incompleteMatches.length > 0) {
                // Finde die früheste unvollständige Runde - verwende deutsche Bezeichnungen wie im Backend
                const roundOrder = ['GROUP_PHASE', 'ROUND_16', 'QUARTER_FINAL', 'SEMI_FINAL', 'THIRD_PLACE', 'FINAL'];
                for (const round of roundOrder) {
                  if (incompleteMatches.some(m => m.round === round)) {
                    currentRound = round; // Verwende direkt die deutsche Bezeichnung
                    break;
                  }
                }
              }
              totalMatches = matches.length;
              completedMatches = matches.filter(m => m.completed).length;
            }
            // Turnier zur Liste hinzufügen, sobald KO-Phase existiert (matches kann leer sein)
            tournamentsWithKnockout.push({
              ...tournament,
              currentRound,
              totalMatches,
              completedMatches
            });
          } catch (err) {
            // Kein K.O.-Phase für dieses Turnier
          }
        }

        setAllTournaments(tournamentsWithKnockout);
      } catch (error) {
        console.error('Fehler beim Laden aller Turniere:', error);
      }
    };
    loadAllTournaments();
  }, [api]);

  return (
    <div className="knockout-container">
      <KnockoutBracket
        tournamentId={tournamentId}
        user={user}
        onBackToGroupPhase={() => { window.location.href = '/tournaments'; }}
      />

      {/* Übersicht aller Turniere mit K.O.-Phase */}
      {allTournaments.length > 0 && (
        <div className="knockout-tournaments-overview">
          <h2 className="knockout-overview-title">Alle Turniere mit K.O.-Phase</h2>
          <div className="knockout-tournaments-grid">
            {allTournaments.slice().reverse().map(tourney => (
              <div
                key={tourney.id}
                className={`knockout-tournament-card ${tourney.id === tournamentId ? 'current' : ''}`}
              >
                <div className="knockout-tournament-header">
                  <h3 className="knockout-tournament-name">{tourney.name}</h3>
                  <span className={`knockout-tournament-status ${tourney.currentRound === 'Abgeschlossen' ? 'completed' : 'active'}`}>
                    {tourney.currentRound}
                  </span>
                </div>

                <div className="knockout-tournament-details">
                  <div className="knockout-tournament-progress">
                    <span className="knockout-progress-text">
                      {tourney.completedMatches} / {tourney.totalMatches} Matches abgeschlossen
                    </span>
                    <div className="knockout-progress-bar">
                      <div
                        className="knockout-progress-fill"
                        style={{ width: `${(tourney.completedMatches / tourney.totalMatches) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {tourney.id !== tournamentId && (
                    <button
                      onClick={() => setTournamentId(tourney.id)}
                      className="knockout-tournament-button"
                    >
                      K.O.-Phase anzeigen
                    </button>
                  )}

                  {tourney.id === tournamentId && (
                    <span className="knockout-current-indicator">Aktuell angezeigt</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnockoutPhase;
